import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';
import { listUsers, resendCredentials } from '../../services/userManagementService';
import { getUserStatus } from '../../utils/statusUtils';
import UserCreateModal from '../shared/UserCreateModal';
import DataTable from '../shared/DataTable';
import SkeletonLoader from '../shared/SkeletonLoader';
import EmptyState from '../shared/EmptyState';
import ErrorMessage from '../shared/ErrorMessage';
import toast from 'react-hot-toast';

/**
 * PatientManagement Component for Clinic Dashboard
 * 
 * Allows physicians and clinic admins to:
 * - View all patients for the selected clinic
 * - Create new patients
 * - Search and filter patients
 * - View patient details
 */
const PatientManagement = () => {
  const { profile } = useAuth();
  const { getEffectiveTenantId, viewMode } = useView();
  const navigate = useNavigate();

  // Get the effective tenant ID (from clinic selector if viewing as clinic)
  const effectiveTenantId = getEffectiveTenantId() || profile?.tenant_id;

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const statusFilter = 'all'; // Future: Add UI controls to change this filter
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [resendingEmail, setResendingEmail] = useState(null);

  // Fetch patients
  const fetchPatients = async () => {
    if (!effectiveTenantId) {
      setError('No clinic selected. Please select a clinic first.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await listUsers({
        tenantId: effectiveTenantId,
        role: 'patient',
        q: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page,
        pageSize,
        sortBy: 'created_at',
        sortOrder: 'desc',
      });

      if (result.error) {
        throw result.error;
      }

      setPatients(result.data?.rows || []);
      setTotalCount(result.data?.total || 0);
    } catch (err) {
      console.error('Error fetching patients:', err);
      setError(err.message || 'Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  // Fetch patients when filters change
  useEffect(() => {
    fetchPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTenantId, searchTerm, statusFilter, page, pageSize]);

  // Handle patient creation
  const handlePatientCreated = () => {
    fetchPatients();
    setCreateModalOpen(false);
  };

  // Handle resend credentials
  const handleResendCredentials = async (userId) => {
    setResendingEmail(userId);
    try {
      const result = await resendCredentials(userId);
      if (result.error) {
        throw result.error;
      }
      // Show password in toast if email might have failed
      if (result.data?.data?.password) {
        toast.success(
          `Credentials email sent! Password: ${result.data.data.password}\n\n⚠️ If email fails, share this password manually.`,
          { duration: 10000 }
        );
      } else {
        toast.success('Credentials email sent successfully!');
      }
    } catch (error) {
      console.error('Error resending credentials:', error);
      toast.error('Failed to resend credentials email');
    } finally {
      setResendingEmail(null);
    }
  };

  // DataTable columns
  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'N/A',
      sortable: true,
    },
    {
      key: 'email',
      header: 'Email',
      render: (row) => row.email || 'N/A',
      sortable: true,
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (row) => row.phone || 'N/A',
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Typography
          variant="body2"
          sx={{
            color:
              row.status === 'active'
                ? 'success.main'
                : row.status === 'pending'
                ? 'warning.main'
                : 'text.secondary',
            fontWeight: 600,
            textTransform: 'capitalize',
          }}
        >
          {getUserStatus(row.status)}
        </Typography>
      ),
      sortable: true,
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (row) =>
        row.created_at
          ? new Date(row.created_at).toLocaleDateString()
          : 'N/A',
      sortable: true,
    },
  ];

  // Actions slot for DataTable
  const actionsSlot = (row) => {
    const userId = row.id || row.user_id;
    return (
      <Tooltip title="Resend credentials email">
        <IconButton
          size="small"
          onClick={() => handleResendCredentials(userId)}
          disabled={resendingEmail === userId}
          color="primary"
        >
          <EmailIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 4 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="Back to Dashboard">
              <IconButton
                onClick={() => navigate('/clinic-dashboard')}
                sx={{ color: 'text.primary' }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Patient Management
            </Typography>
            {viewMode.active && viewMode.tenantName && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({viewMode.tenantName})
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
            disabled={!effectiveTenantId}
            sx={{ minHeight: 44 }}
          >
            Add Patient
          </Button>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Alert if no clinic selected */}
        {!effectiveTenantId && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Please select a clinic from the clinic selector to manage patients.
          </Alert>
        )}

        {/* Search and Filter */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1); // Reset to first page on search
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {totalCount} patient{totalCount !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Patients Table */}
        {loading ? (
          <SkeletonLoader variant="table" count={5} />
        ) : error ? (
          <ErrorMessage
            title="Unable to load patients"
            message={error}
            actionLabel="Retry"
            onAction={fetchPatients}
            severity="error"
          />
        ) : patients.length === 0 ? (
          <EmptyState
            title="No patients found"
            description={
              searchTerm || statusFilter !== 'all'
                ? "No patients match your search criteria. Try adjusting your filters."
                : "You haven't created any patients yet. Click 'Add Patient' to get started."
            }
            actionLabel={!searchTerm && statusFilter === 'all' ? 'Add First Patient' : undefined}
            onAction={!searchTerm && statusFilter === 'all' ? () => setCreateModalOpen(true) : undefined}
            variant="illustrated"
          />
        ) : (
          <DataTable
            columns={columns}
            rows={patients}
            total={totalCount}
            page={page - 1}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage + 1)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            getRowId={(row) => row.id || row.user_id || `${row.email}-${row.first_name}`}
            emptyMessage="No patients found"
            ariaLabel="patients table"
            onRetry={fetchPatients}
            actionsSlot={actionsSlot}
          />
        )}
      </Box>

      {/* Create Patient Modal */}
      <UserCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onUserCreated={handlePatientCreated}
        role="patient"
      />
    </Box>
  );
};

export default PatientManagement;

